import {
  alertRequiresNavAttention,
  areEquivalentAlertPages,
  getAlertPageKey,
  getOrderedTriggeredAlertsForToasts,
} from '@aloft/toolkit-shared';

import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAlertsRef } from '../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectCommentaryV2 } from '../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectIsInspectifyOrder } from '../redux/features/selectors/orderSelectors.ts';
import {
  selectPendingNavBlockToastTypes,
  selectTriggeredAlerts,
  selectToastSuppressedAlertTypes,
} from '../redux/features/selectors/uiSelectors.ts';
import { clearPendingNavBlockToastTypes } from '../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import { applyVisibleAlertToasts } from './useToaster/applyVisibleAlertToasts.ts';
import { dismissInactiveAlertToasts } from './useToaster/dismissAllToasts.ts';
import { useToaster } from './useToaster/useToaster.tsx';
import { routeToStepMap } from './validation/consts.ts';
import { useAppraisifyNavigation } from './validation/useAppraisifyNavigation.ts';

export const useTriggeredAlertToasts = () => {
  const dispatch = useAppDispatch();
  const makeToast = useToaster();
  const triggeredAlerts = useAppSelector(selectTriggeredAlerts);
  const pendingNavBlockToastTypes = useAppSelector(selectPendingNavBlockToastTypes);
  const toastSuppressedAlertTypes = useAppSelector(selectToastSuppressedAlertTypes);
  const suppressedAlertTypes = useMemo(
    () => new Set(toastSuppressedAlertTypes),
    [toastSuppressedAlertTypes],
  );
  const pendingNavBlockToastTypeSet = useMemo(
    () => new Set(pendingNavBlockToastTypes),
    [pendingNavBlockToastTypes],
  );
  const triggeredAlertTypeSet = useMemo(() => new Set(triggeredAlerts), [triggeredAlerts]);
  const alertsCommentary = useAppSelector(selectCommentaryV2).alerts;
  const alertsRef = useAlertsRef();
  const inspectifyOrder = useAppSelector(selectIsInspectifyOrder);
  const { pathname } = useLocation();
  const { getCurrentAppraisifyRoute, isRouteWithinAlertNavFrontier } = useAppraisifyNavigation();
  const currentAppraisifyRoute = getCurrentAppraisifyRoute(pathname);

  useEffect(() => {
    if (!inspectifyOrder) return;

    if (triggeredAlerts.length === 0) {
      dismissInactiveAlertToasts(new Set());
      return;
    }

    const { adjustments, comparable, depreciatedCost } = alertsRef.current;
    const alertsPerSection = { adjustments, comparable, depreciatedCost };

    const orderedTriggeredAlerts = getOrderedTriggeredAlertsForToasts(
      alertsPerSection,
      triggeredAlerts,
      routeToStepMap,
      alertsCommentary,
    );
    const flatAlerts = Object.values(alertsPerSection).flat();
    const alertsToToast =
      orderedTriggeredAlerts.length > 0
        ? orderedTriggeredAlerts
        : triggeredAlerts
            .map((type) => flatAlerts.find((alert) => alert.type === type))
            .filter((alert): alert is NonNullable<typeof alert> =>
              Boolean(alert && alertRequiresNavAttention(alert, alertsCommentary)),
            );

    const pendingOnCurrentPage =
      currentAppraisifyRoute &&
      pendingNavBlockToastTypes.some((type) => {
        const alert = flatAlerts.find((candidate) => candidate.type === type);
        return (
          alert &&
          areEquivalentAlertPages(getAlertPageKey(alert, routeToStepMap), currentAppraisifyRoute)
        );
      });

    const visibleAlerts = (currentAppraisifyRoute
      ? alertsToToast.filter((alert) => {
          const alertPage = getAlertPageKey(alert, routeToStepMap);
          if (areEquivalentAlertPages(alertPage, currentAppraisifyRoute)) {
            return Boolean(pendingOnCurrentPage && triggeredAlertTypeSet.has(alert.type));
          }
          return isRouteWithinAlertNavFrontier(
            alertPage,
            currentAppraisifyRoute,
            alertsPerSection,
          );
        })
      : alertsToToast.filter((alert) => pendingNavBlockToastTypeSet.has(alert.type))
    ).filter((alert) => !suppressedAlertTypes.has(alert.type));

    if (visibleAlerts.length === 0) {
      if (triggeredAlerts.length === 0) {
        dismissInactiveAlertToasts(new Set());
      }
      return;
    }

    applyVisibleAlertToasts(visibleAlerts, makeToast);

    const onPendingAlertPage =
      currentAppraisifyRoute &&
      pendingNavBlockToastTypes.some((type) => {
        const alert = flatAlerts.find((candidate) => candidate.type === type);
        return (
          alert &&
          areEquivalentAlertPages(getAlertPageKey(alert, routeToStepMap), currentAppraisifyRoute)
        );
      });

    if (
      onPendingAlertPage &&
      visibleAlerts.some((alert) => pendingNavBlockToastTypeSet.has(alert.type))
    ) {
      dispatch(clearPendingNavBlockToastTypes());
    }
  }, [
    alertsCommentary,
    currentAppraisifyRoute,
    dispatch,
    inspectifyOrder,
    isRouteWithinAlertNavFrontier,
    makeToast,
    pendingNavBlockToastTypeSet,
    pendingNavBlockToastTypes.length,
    suppressedAlertTypes,
    triggeredAlerts,
  ]);
};
